import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from '../../../components/ui/notify';

import { decodeData } from '../../../utils/encodeAndEncode';
import { getClassroomAssignmentQuiz, getUserClassroomAssignmentById, submitClassroomAssignmentQuiz, saveIndividualQuestionAnswer } from '../../../services/user/classroom.services';
import useClassroomRealtime from '../../../hooks/useClassroomRealtime';
import { CLASSROOM_REALTIME_EVENTS } from '../../../services/classroomRealtime.service';

function AssignmentQuiz() {
  const { encodedAssignmentId, encodedQuestionId } = useParams();
  const [searchParams] = useSearchParams();
  const encodedClassroomId = searchParams.get('classroomid');
  const navigate = useNavigate();

  // Decode the IDs
  const assignmentId = decodeData(decodeURIComponent(encodedAssignmentId));
  const quizId = decodeData(decodeURIComponent(encodedQuestionId));
  const classroomId = encodedClassroomId ? decodeData(decodeURIComponent(encodedClassroomId)) : null;

  // State management
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [quizStarted, setQuizStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [availability, setAvailability] = useState('checking');
  const availabilityRequest = useRef(0);
  const [quizData, setQuizData] = useState({
    duration: 5,
    pass_percentage: 60,
    total_questions: 0,
    is_attempted: 0,
    result: null
  });
  const [submissionResult, setSubmissionResult] = useState(null);
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState({});

  useEffect(() => {
    availabilityRequest.current += 1;
    setAvailability('checking');
    setQuestions([]);
    getAssignmentQuizQuestions();
    // Reset component state when quiz changes
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setQuizStarted(false);
    setSubmissionResult(null);
  }, [assignmentId, quizId]);

  const checkAvailability = async () => {
    const requestId = availabilityRequest.current;
    try {
      const response = await getUserClassroomAssignmentById({ class_id: classroomId, assignment_id: assignmentId });
      if (requestId !== availabilityRequest.current) return false;
      if (!response?.success) throw new Error('This assignment is not available');
      setAvailability('available');
      return true;
    } catch (error) {
      if (requestId !== availabilityRequest.current) return false;
      setAvailability('unavailable');
      setQuestions([]);
      return false;
    }
  };

  useEffect(() => {
    if (!assignmentId || !classroomId) return undefined;
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        checkAvailability().then((available) => {
          if (available && availability === 'unavailable') getAssignmentQuizQuestions();
        });
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const timer = window.setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.clearInterval(timer);
    };
  }, [assignmentId, classroomId, availability]);

  useClassroomRealtime(classroomId, {
    [CLASSROOM_REALTIME_EVENTS.ASSIGNMENT_VISIBILITY_CHANGED]: (payload) => {
      if (Number(payload.assignmentId ?? payload.assignment_id) === Number(assignmentId) && Number(payload.is_visible) === 0) {
        availabilityRequest.current += 1;
        setAvailability('unavailable');
        setQuestions([]);
      }
    },
    [CLASSROOM_REALTIME_EVENTS.ASSIGNMENT_UPDATED]: (payload) => {
      if (Number(payload.assignmentId ?? payload.assignment_id) !== Number(assignmentId)) return;
      if (Number(payload.is_visible) === 0) {
        availabilityRequest.current += 1;
        setAvailability('unavailable');
        setQuestions([]);
      }
    },
  });

  const getAssignmentQuizQuestions = async () => {
    try {
      if (!(await checkAvailability())) return;
      const response = await getClassroomAssignmentQuiz({
        assignment_id: assignmentId,
        quiz_id: quizId
      });

      if (response.success) {
        setQuestions(response.data);
        setQuizData({
          duration: response.duration || 5,
          pass_percentage: response.pass_percentage || 60,
          total_questions: response.total_questions || 0,
          is_attempted: response.is_attempted || 0,
          result: response.result || null
        });
        setTimeLeft(response.duration * 60 || 300); // Convert minutes to seconds
      }
    } catch (error) {
      setAvailability('unavailable');
      setQuestions([]);
    }
  };

  // Timer logic
  React.useEffect(() => {
    if (quizStarted && timeLeft > 0 && !showResults) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowResults(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quizStarted, timeLeft, showResults]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleStartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setShowResults(false);
    setTimeLeft(quizData.duration * 60 || 300);
    setQuizStarted(true);
  };

  const handleOptionSelect = (questionId, optionId, answerType) => {
    setSelectedAnswers(prev => {
      const currentSelections = prev[questionId] || [];
      let updatedSelections;

      if (answerType === 'single') {
        // For single choice: replace the selection (radio button behavior)
        updatedSelections = [optionId];
      } else {
        // For multiple choice: toggle the selection (checkbox behavior)
        updatedSelections = currentSelections.includes(optionId)
          ? currentSelections.filter(id => id !== optionId)
          : [...currentSelections, optionId];
      }

      return {
        ...prev,
        [questionId]: updatedSelections
      };
    });
  };

  const handleNext = async () => {
    if (availability !== 'available' || !(await checkAvailability())) return;
    const question = questions[currentQuestion];
    const selectedOptionIds = selectedAnswers[question.id] || [];

    if (selectedOptionIds.length === 0) {
      toast.warning('Please select an answer before proceeding.');
      return;
    }

    // Check if answer is already saved
    if (savedAnswers[currentQuestion]) {
      moveToNextQuestion();
      return;
    }

    setIsSavingAnswer(true);

    try {
      // For assignment quizzes, we need to save each selected option
      // Since it's multiple choice, we'll save the first selected option as the primary answer
      const primaryOption = selectedOptionIds[0];

      const payload = {
        class_id: classroomId,
        assessment_id: assignmentId, // Using assignmentId as assessment_id for consistency
        user_id: JSON.parse(localStorage.getItem('user'))?.id,
        question_id: question.id,
        option: primaryOption
      };

      const response = await saveIndividualQuestionAnswer(payload);

      if (response?.success) {
        // Mark this answer as saved
        setSavedAnswers(prev => ({
          ...prev,
          [currentQuestion]: true
        }));

        toast.success(`Answer for question ${currentQuestion + 1} saved successfully!`, {
          toastId: `answer-saved-${currentQuestion}`,
          autoClose: 2000,
        });

        // Move to next question
        moveToNextQuestion();
      } else {
        toast.error('Failed to save answer. Please try again.');
      }
    } catch (error) {
      console.error(`❌ Error saving answer for assignment question ${currentQuestion + 1}:`, error);
      toast.error('Error saving answer. Please try again.');
    } finally {
      setIsSavingAnswer(false);
    }
  };

  const moveToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (availability !== 'available') return;
    try {
      const answers = questions.map(question => {
        const selectedOptionIds = selectedAnswers[question.id] || [];
        const correctOptions = question.options.filter(opt => opt.is_correct);

        return {
          question_id: question.id,
          selected_option_id: selectedOptionIds,
          correct_option_id: correctOptions.map(opt => opt.id),
          correct_value: correctOptions.map(opt => opt.content_value)
        };
      });

      const payload = {
        assignment_id: assignmentId,
        quiz_id: quizId,
        answers: answers
      };


      const response = await submitClassroomAssignmentQuiz(payload);

      setSubmissionResult(response.result);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting assignment quiz:', error);
      if (error.message?.toLowerCase().includes('not available')) {
        setAvailability('unavailable');
        setQuestions([]);
      }
    }
  };

  const handleBackToAssignment = () => {
    navigate(`/user/assignmentsQuestions/${encodedAssignmentId}?classroomid=${encodedClassroomId}`);
  };

  const renderQuestionContent = (question) => {
    switch (question.question_content_type) {
      case 'image':
        return (
          <div className={"tw-mb-[25px] tw-w-full tw-pb-[20px] tw-text-[1.2rem] tw-leading-[1.6] tw-text-[#212529] [word-wrap:break-word] [overflow-wrap:break-word] [&_img.question-image]:tw-max-w-full [&_img.question-image]:tw-h-auto [&_img.question-image]:tw-max-h-[300px] [&_img.question-image]:tw-object-contain [&_img.question-image]:tw-rounded-[8px] question-content"}>
            <div className="tw-text-[1.2rem] tw-font-medium tw-leading-[1.5] tw-text-[#333] tw-m-0 tw-p-0 tw-mb-[15px] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] question-text">{question.question}</div>
            {question.question_media_url && (
              <div className="tw-w-full tw-flex tw-justify-center tw-items-center tw-mt-[10px] tw-p-[10px] tw-rounded-[8px] question-media-container">
                <img
                  src={question.question_media_url}
                  alt={question.question}
                  className="question-image"
                />
              </div>
            )}
          </div>
        );
      case 'audio':
        return (
          <div className={"tw-mb-[25px] tw-w-full tw-pb-[20px] tw-text-[1.2rem] tw-leading-[1.6] tw-text-[#212529] [word-wrap:break-word] [overflow-wrap:break-word] [&_img.question-image]:tw-max-w-full [&_img.question-image]:tw-h-auto [&_img.question-image]:tw-max-h-[300px] [&_img.question-image]:tw-object-contain [&_img.question-image]:tw-rounded-[8px] question-content"}>
            <div className="tw-text-[1.2rem] tw-font-medium tw-leading-[1.5] tw-text-[#333] tw-m-0 tw-p-0 tw-mb-[15px] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] question-text">{question.question}</div>
            {question.question_media_url && (
              <div className="tw-w-full tw-flex tw-justify-center tw-items-center tw-mt-[10px] tw-p-[10px] tw-rounded-[8px] question-media-container">
                <audio controls className="tw-w-full tw-max-w-[400px] tw-m-[10px_0] question-audio">
                  <source src={question.question_media_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
          </div>
        );
      case 'text':
      default:
        return (
          <div className={"tw-mb-[25px] tw-w-full tw-pb-[20px] tw-text-[1.2rem] tw-leading-[1.6] tw-text-[#212529] [word-wrap:break-word] [overflow-wrap:break-word] [&_img.question-image]:tw-max-w-full [&_img.question-image]:tw-h-auto [&_img.question-image]:tw-max-h-[300px] [&_img.question-image]:tw-object-contain [&_img.question-image]:tw-rounded-[8px] question-content"}>
            <div className="tw-text-[1.2rem] tw-font-medium tw-leading-[1.5] tw-text-[#333] tw-m-0 tw-p-0 tw-mb-[15px] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] question-text">{question.question}</div>
          </div>
        );
    }
  };

  const getOptionLetter = (index) => {
    return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
  };

  const renderOptionContent = (option) => {
    switch (option.content_type) {
      case 'image':
        return (
          <div className="tw-flex tw-flex-col [flex:1] tw-min-w-[0] option-content-wrapper">
            <span className="tw-w-full [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] tw-text-[1rem] tw-leading-[1.4] tw-text-[#333] [flex:1] option-text">{option.content_value}</span>
            <div className="tw-w-full tw-mt-[10px] option-media-container">
              {option.media_url && (
                <img
                  src={option.media_url}
                  alt={option.content_value}
                  className="option-image"
                />
              )}
            </div>
          </div>
        );
      case 'audio':
        return (
          <div className="tw-flex tw-flex-col [flex:1] tw-min-w-[0] option-content-wrapper">
            <span className="tw-w-full [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] tw-text-[1rem] tw-leading-[1.4] tw-text-[#333] [flex:1] option-text">{option.content_value}</span>
            <div className="tw-w-full tw-mt-[10px] option-media-container">
              {option.media_url && (
                <audio controls className="option-audio">
                  <source src={option.media_url} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>
          </div>
        );
      case 'text':
      default:
        return (
          <div className="tw-flex tw-flex-col [flex:1] tw-min-w-[0] option-content-wrapper">
            <span className="tw-w-full [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] [hyphens:auto] tw-text-[1rem] tw-leading-[1.4] tw-text-[#333] [flex:1] option-text">{option.content_value}</span>
          </div>
        );
    }
  };

  const renderResults = () => {
    if (!showResults || !submissionResult) return null;

    return (
      <div className={"tw-max-w-full tw-bg-[#fff] tw-rounded-[8px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] tw-m-[0_auto] max-[768px]:tw-p-[15px] [&_.ui-button-warning]:tw-bg-[#ffc107] [&_.ui-button-warning]:tw-text-[#000] [&_.ui-button-warning]:tw-border-0 [&_.ui-button-warning:hover]:tw-bg-[#e0a800] [&_.ui-button-warning:hover]:tw-text-[#000] [&_.ui-button-primary]:tw-bg-[#2196f3] [&_.ui-button-primary]:tw-text-[white] [&_.ui-button-primary]:tw-border-0 [&_.ui-button-primary:hover]:tw-bg-[#1976d2] [&_.ui-button-primary:hover]:tw-text-[white] [&_.ui-button]:tw-inline-flex [&_.ui-button]:tw-items-center [&_.ui-button]:tw-gap-[8px] [&_.ui-button]:tw-p-[10px_24px] [&_.ui-button]:tw-font-medium [&_.ui-button]:tw-rounded-[4px] [&_.ui-button]:[transition:all_0.3s_ease] [&_.ui-button]:tw-w-auto [&_.ui-button]:tw-justify-center quiz-container"}>
        <div className={"tw-bg-[white] tw-rounded-[12px] tw-p-[30px] tw-mt-[30px] tw-text-center tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.1)] [&_h2]:tw-text-[#1a237e] [&_h2]:tw-mb-[30px] [&_h2]:tw-font-semibold quiz-results"}>
          <h2>Quiz Results</h2>

          <div className="max-[768px]:tw-w-[120px] max-[768px]:tw-h-[120px] tw-w-[150px] tw-h-[150px] tw-rounded-[50%] tw-border-[8px] tw-border-solid tw-border-[#2196f3] tw-flex tw-flex-col tw-justify-center tw-items-center tw-m-[0_auto_30px] tw-bg-[white] tw-p-[10px] tw-box-border tw-overflow-hidden score-circle">
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-max-w-full tw-p-[0_6px] tw-text-center tw-text-[2.5rem] tw-font-bold tw-text-[#2196f3] score-percentage">{submissionResult.percentage}.0%</div>
            <div className="tw-text-[#666] tw-text-[1rem] tw-mt-[5px] score-label">Score</div>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-[15px] tw-max-w-[400px] tw-m-[0_auto] result-stats">
            <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item"}>
              <Icon icon="mdi:help-circle-outline" className="tw-text-[20px] stat-icon" />
              <span className="[flex:1] tw-text-left tw-font-medium stat-text">Total Questions: {submissionResult.total_questions}</span>
            </div>

            <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item"}>
              <Icon icon="mdi:checkbox-marked-circle-outline" className="tw-text-[20px] stat-icon" />
              <span className="[flex:1] tw-text-left tw-font-medium stat-text">Attempted Questions: {submissionResult.attempted_questions}</span>
            </div>

            <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item"}>
              <Icon icon="mdi:clock-outline" className="tw-text-[20px] stat-icon" />
              <span className="[flex:1] tw-text-left tw-font-medium stat-text">Passing Score: {quizData.pass_percentage}%</span>
            </div>

            <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item correct"}>
              <Icon icon="mdi:check-circle" className="tw-text-[20px] stat-icon" />
              <span className="[flex:1] tw-text-left tw-font-medium stat-text">Correct Answers: {submissionResult.correct_answers}</span>
            </div>

            <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item incorrect"}>
              <Icon icon="mdi:close-circle" className="tw-text-[20px] stat-icon" />
              <span className="[flex:1] tw-text-left tw-font-medium stat-text">Wrong Answers: {submissionResult.wrong_answers}</span>
            </div>
          </div>

          <div className={`tw-inline-flex tw-items-center tw-gap-[8px] tw-p-[8px_20px] tw-rounded-[20px] tw-font-medium tw-mt-[20px] [&.passed]:tw-bg-[#e8f5e9] [&.passed]:tw-text-[#2e7d32] [&.failed]:tw-bg-[#ffebee] [&.failed]:tw-text-[#c62828] pass-badge   ${submissionResult.pass_status === "pass" ? "passed" : "failed"}`}>
            <Icon icon={submissionResult.pass_status === "pass" ? "mdi:check-circle" : "mdi:close-circle"} />
            <span>{submissionResult.pass_status === "pass" ? "Passed" : "Failed"}</span>
          </div>

          <div className={"tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-flex tw-justify-center tw-mt-6"}>
            <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/2">
              <button
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(254_243_199_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(253_230_138_/_var(--tw-bg-opacity,_1))] ui-button ui-button-warning tw-mb-2"}
                onClick={handleStartQuiz}
              >
                <Icon icon="mdi:refresh" className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"} />
                Start Again
              </button>
              <button
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                onClick={handleBackToAssignment}
              >
                <Icon icon="mdi:arrow-left" className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"} />
                Back to Assignment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (showResults && submissionResult) {
    return renderResults();
  }

  if (!quizStarted) {
    return (
      <div className={"tw-max-w-full tw-bg-[#fff] tw-rounded-[8px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] tw-m-[0_auto] max-[768px]:tw-p-[15px] [&_.ui-button-warning]:tw-bg-[#ffc107] [&_.ui-button-warning]:tw-text-[#000] [&_.ui-button-warning]:tw-border-0 [&_.ui-button-warning:hover]:tw-bg-[#e0a800] [&_.ui-button-warning:hover]:tw-text-[#000] [&_.ui-button-primary]:tw-bg-[#2196f3] [&_.ui-button-primary]:tw-text-[white] [&_.ui-button-primary]:tw-border-0 [&_.ui-button-primary:hover]:tw-bg-[#1976d2] [&_.ui-button-primary:hover]:tw-text-[white] [&_.ui-button]:tw-inline-flex [&_.ui-button]:tw-items-center [&_.ui-button]:tw-gap-[8px] [&_.ui-button]:tw-p-[10px_24px] [&_.ui-button]:tw-font-medium [&_.ui-button]:tw-rounded-[4px] [&_.ui-button]:[transition:all_0.3s_ease] [&_.ui-button]:tw-w-auto [&_.ui-button]:tw-justify-center quiz-container tw-p-2 md:tw-p-12"}>
        <div className="tw-text-center tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] tw-m-[0_auto] quiz-intro tw-p-0 md:tw-px-12 md:tw-py-2">
          <h1>Assignment Quiz</h1>

          {quizData.is_attempted === 1 && (
            <div className={"[&_.score-percentage-value]:tw-text-[2rem] [&_.score-percentage--decimal_.score-percentage-value]:tw-text-[1.25rem] [&_.score-percentage--medium_.score-percentage-value]:tw-text-[1.65rem] tw-bg-[#f8f9fa] tw-rounded-[12px] tw-p-[20px] tw-m-[20px_auto] tw-max-w-[500px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.05)] [&_h3]:tw-text-[#1a237e] [&_h3]:tw-text-[1.5rem] [&_h3]:tw-font-semibold [&_.score-circle]:tw-w-[120px] [&_.score-circle]:tw-h-[120px] [&_.score-circle]:tw-border-[6px] [&_.score-percentage]:tw-text-[2rem] [&_.result-stats]:tw-gap-[10px] [&_.stat-item]:tw-p-[8px_15px] [&_.stat-item]:tw-text-[0.95rem] [&_.pass-badge]:tw-mt-[15px] [&_.pass-badge]:tw-text-[0.95rem] [&_.pass-badge]:tw-p-[6px_15px] previous-attempt-results"}>
              <h3 className="tw-mb-6">Previous Attempt Results</h3>
              <div className="max-[768px]:tw-w-[120px] max-[768px]:tw-h-[120px] tw-w-[150px] tw-h-[150px] tw-rounded-[50%] tw-border-[8px] tw-border-solid tw-border-[#2196f3] tw-flex tw-flex-col tw-justify-center tw-items-center tw-m-[0_auto_30px] tw-bg-[white] tw-p-[10px] tw-box-border tw-overflow-hidden score-circle tw-mb-6">
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-max-w-full tw-p-[0_6px] tw-text-center tw-text-[2.5rem] tw-font-bold tw-text-[#2196f3] score-percentage">{quizData.result?.percentage}%</div>
                <div className="tw-text-[#666] tw-text-[1rem] tw-mt-[5px] score-label">Score</div>
              </div>

              <div className="tw-flex tw-flex-col tw-gap-[15px] tw-max-w-[400px] tw-m-[0_auto] result-stats">
                <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item"}>
                  <Icon icon="mdi:help-circle-outline" className="tw-text-[20px] stat-icon" />
                  <span className="[flex:1] tw-text-left tw-font-medium stat-text">Total Questions: {quizData.result?.total_questions}</span>
                </div>

                <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item"}>
                  <Icon icon="mdi:checkbox-marked-circle-outline" className="tw-text-[20px] stat-icon" />
                  <span className="[flex:1] tw-text-left tw-font-medium stat-text">Attempted Questions: {quizData.result?.attempted_questions}</span>
                </div>

                <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item correct"}>
                  <Icon icon="mdi:check-circle" className="tw-text-[20px] stat-icon" />
                  <span className="[flex:1] tw-text-left tw-font-medium stat-text">Correct Answers: {quizData.result?.correct_answers}</span>
                </div>

                <div className={"tw-flex tw-items-center tw-p-[12px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-gap-[10px] [&.correct]:tw-text-[#2e7d32] [&.incorrect]:tw-text-[#c62828] [&.time]:tw-text-[#1565c0] stat-item incorrect"}>
                  <Icon icon="mdi:close-circle" className="tw-text-[20px] stat-icon" />
                  <span className="[flex:1] tw-text-left tw-font-medium stat-text">Wrong Answers: {quizData.result?.wrong_answers}</span>
                </div>
              </div>

              <div className={`tw-inline-flex tw-items-center tw-gap-[8px] tw-p-[8px_20px] tw-rounded-[20px] tw-font-medium tw-mt-[20px] [&.passed]:tw-bg-[#e8f5e9] [&.passed]:tw-text-[#2e7d32] [&.failed]:tw-bg-[#ffebee] [&.failed]:tw-text-[#c62828] pass-badge   ${quizData.result?.pass_status === "pass" ? "passed" : "failed"}`}>
                <Icon icon={quizData.result?.pass_status === "pass" ? "mdi:check-circle" : "mdi:close-circle"} />
                <span>{quizData.result?.pass_status === "pass" ? "Passed" : "Failed"}</span>
              </div>
            </div>
          )}

          <div className="max-[768px]:tw-flex-col max-[768px]:tw-gap-4 tw-flex tw-flex-wrap tw-gap-6 tw-m-[1.5rem_0] tw-justify-center quiz-info tw-mt-6">
            <div className="tw-flex tw-items-center tw-gap-2 info-item">
              <Icon icon="mdi:help-circle-outline" className="tw-text-[1.25rem] tw-text-[#0d6efd] info-icon" />
              <span>Total Questions: {quizData.total_questions}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 info-item">
              <Icon icon="mdi:clock-outline" className="tw-text-[1.25rem] tw-text-[#0d6efd] info-icon" />
              <span>Duration: {quizData.duration} minutes</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 info-item">
              <Icon icon="mdi:trophy-outline" className="tw-text-[1.25rem] tw-text-[#0d6efd] info-icon" />
              <span>Passing Score: {quizData.pass_percentage}%</span>
            </div>
          </div>

          <div className={"tw-text-left tw-m-[2rem_0] tw-bg-[#f8f9fa] tw-rounded-[8px] [&_ul]:[list-style-type:none] [&_ul]:tw-p-0 [&_li]:tw-m-[1rem_0] [&_li]:tw-pl-6 [&_li]:tw-relative [&_li:before]:[content:\"•\"] [&_li:before]:tw-text-[#4a90e2] [&_li:before]:tw-absolute [&_li:before]:tw-left-0 instructions tw-p-2 md:tw-p-6"}>
            <h2>Instructions:</h2>
            <ul>
              <li>Read each question carefully before answering</li>
              <li>You can go back to previous questions</li>
              <li>Timer will start once you begin the quiz</li>
              <li>Questions may contain text, images, or audio content</li>
            </ul>
          </div>

          <div className={"tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-flex tw-justify-center"}>
            <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/2">
              <button
                className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button tw-mb-2   ${quizData.is_attempted ? "[--tw-bg-opacity:1] tw-bg-[rgb(254_243_199_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(253_230_138_/_var(--tw-bg-opacity,_1))] ui-button-warning" : "[&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button-primary"}`}
                onClick={handleStartQuiz}
              >
                <Icon icon="mdi:play" className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"} />
                {quizData.is_attempted ? 'Start Again' : 'Start Quiz'}
              </button>
              <button
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-secondary"}
                onClick={handleBackToAssignment}
              >
                <Icon icon="mdi:arrow-left" className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"} />
                Back to Assignment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  if (availability !== 'available') {
    return <div className="tw-mx-auto tw-max-w-xl tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-8 tw-text-center">
      <h4>{availability === 'checking' ? 'Loading assignment' : 'Assignment not available'}</h4>
      {availability === 'unavailable' && <p>This assignment is hidden or you no longer have access to it.</p>}
      {availability === 'unavailable' && <button className="ui-button ui-button-primary" onClick={() => navigate(-1)}>Back to assignments</button>}
    </div>;
  }

  if (!question) {
    return <div>Loading...</div>;
  }

  return (
    <div className={"tw-max-w-full tw-bg-[#fff] tw-rounded-[8px] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.1)] [overflow-wrap:break-word] [word-wrap:break-word] [word-break:break-word] tw-m-[0_auto] max-[768px]:tw-p-[15px] [&_.ui-button-warning]:tw-bg-[#ffc107] [&_.ui-button-warning]:tw-text-[#000] [&_.ui-button-warning]:tw-border-0 [&_.ui-button-warning:hover]:tw-bg-[#e0a800] [&_.ui-button-warning:hover]:tw-text-[#000] [&_.ui-button-primary]:tw-bg-[#2196f3] [&_.ui-button-primary]:tw-text-[white] [&_.ui-button-primary]:tw-border-0 [&_.ui-button-primary:hover]:tw-bg-[#1976d2] [&_.ui-button-primary:hover]:tw-text-[white] [&_.ui-button]:tw-inline-flex [&_.ui-button]:tw-items-center [&_.ui-button]:tw-gap-[8px] [&_.ui-button]:tw-p-[10px_24px] [&_.ui-button]:tw-font-medium [&_.ui-button]:tw-rounded-[4px] [&_.ui-button]:[transition:all_0.3s_ease] [&_.ui-button]:tw-w-auto [&_.ui-button]:tw-justify-center quiz-container"}>
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-[24px] tw-p-[16px_20px] tw-bg-[#f8f9fa] tw-rounded-[8px] tw-shadow-[0_1px_3px_rgba(0,_0,_0,_0.1)] quiz-header">
        <div className="tw-flex tw-items-center tw-gap-[4px] tw-text-[1.1rem] tw-font-medium question-progress">
          <span className="tw-text-[#2196f3] tw-font-semibold current-question">{currentQuestion + 1}</span>
          <span className="tw-text-[#666] tw-text-[0.9rem] tw-font-medium total-questions">/{questions.length}</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-[8px] tw-text-[1.1rem] tw-text-[#dc3545] tw-font-medium timer">
          <Icon icon="mdi:clock-outline" className="tw-text-[1.3rem] timer-icon" />
          {formatTime(timeLeft)}
        </div>
      </div>

      <div className="tw-bg-[#fff] tw-rounded-[12px] tw-p-[25px] tw-mb-[24px] tw-shadow-[0_2px_8px_rgba(0,_0,_0,_0.05)] question-container">
        {renderQuestionContent(question)}

        <div className="tw-pt-[20px] tw-grid tw-gap-4 options-container">
          <div className="tw-text-[1rem] tw-text-[#666] tw-mb-[16px] options-label">
            {question.answer_type === 'single'
              ? 'Select the right answer:'
              : 'Select ALL correct answers (missing any one makes it wrong):'}
            <small className="!tw-text-[var(--secondary-color)] tw-ms-2 tw-text-slate-500">
              ({question.answer_type === 'single' ? 'Single Choice' : 'Select Multiple'})
            </small>
          </div>
          {question.options.map((option, index) => (
            <div
              key={option.id}
              className={`tw-flex tw-items-start tw-p-[15px] tw-border-[1px] tw-border-solid tw-border-[#e0e0e0] tw-rounded-[8px] tw-cursor-pointer [transition:all_0.3s_ease] tw-bg-[#f8f9fa] tw-mb-[12px] tw-w-full tw-relative tw-gap-[15px] [&:last-child]:tw-mb-0 [&:hover]:tw-bg-[#e9ecef] [&:hover]:[transform:translateY(-1px)] [&:hover]:tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.05)] [&:hover]:tw-border-[#4a90e2] [&.selected]:tw-bg-[#e3f2fd] [&.selected]:tw-border-[#2196f3] [&.selected]:tw-shadow-[0_2px_4px_rgba(33,_150,_243,_0.1)] [&.selected_.option-letter]:tw-bg-[#2196f3] [&.selected_.option-letter]:tw-text-[white] [&:hover_.option-letter]:tw-bg-[#dee2e6] [&_img.option-image]:tw-max-w-full [&_img.option-image]:tw-h-auto [&_img.option-image]:tw-max-h-[150px] [&_img.option-image]:tw-object-contain [&_img.option-image]:tw-rounded-[4px] [&_audio.option-audio]:tw-w-full [&_audio.option-audio]:tw-max-w-[300px] [&_audio.option-audio]:tw-mt-[8px] max-[768px]:tw-p-[12px] option   ${(selectedAnswers[question.id] || []).includes(option.id) ? "selected" : ""}`}
              onClick={() => handleOptionSelect(question.id, option.id, question.answer_type)}
            >
              <span className="tw-min-w-[28px] tw-h-[28px] tw-flex tw-items-center tw-justify-center tw-rounded-[50%] tw-bg-[#e9ecef] tw-text-[1rem] tw-font-semibold tw-text-[#666] tw-shrink-[0] tw-mt-[2px] option-letter">{getOptionLetter(index)}</span>
              {renderOptionContent(option)}
            </div>
          ))}
        </div>

        <div className="max-[768px]:tw-flex-col tw-flex tw-justify-between tw-mt-[24px] tw-gap-[10px] tw-w-full tw-pt-[16px] tw-border-t-[1px] tw-border-solid tw-border-t-[#eee] navigation-buttons">
          <div className="[flex:1] tw-flex tw-justify-start nav-buttons-left">
            {currentQuestion > 0 && (
              <button className={"tw-text-[#333] tw-bg-[#6c757d] [&:hover]:tw-bg-[#d0d0d0] [&:not(:disabled):hover]:[transform:translateY(-2px)] max-[768px]:tw-w-full max-[768px]:tw-justify-center tw-inline-flex tw-items-center tw-gap-[5px] tw-p-[8px_20px] tw-border-0 tw-rounded-[4px] tw-cursor-pointer [transition:all_0.3s_ease] tw-font-medium tw-min-w-[100px] tw-justify-center tw-text-[1rem] [&:disabled]:tw-opacity-[0.6] [&:disabled]:tw-cursor-not-allowed [&:disabled]:tw-pointer-events-none nav-btn prev-btn"} onClick={handlePrevious}>
                <Icon icon="mdi:chevron-left" />
                Previous
              </button>
            )}
          </div>
          <div className="[flex:1] tw-flex tw-justify-end nav-buttons-right">
            {!isLastQuestion ? (
              <button
                className={"tw-text-[white] tw-bg-[#4a90e2] [&:hover]:tw-bg-[#1976d2] [&:not(:disabled):hover]:[transform:translateY(-2px)] max-[768px]:tw-w-full max-[768px]:tw-justify-center tw-inline-flex tw-items-center tw-gap-[5px] tw-p-[8px_20px] tw-border-0 tw-rounded-[4px] tw-cursor-pointer [transition:all_0.3s_ease] tw-font-medium tw-min-w-[100px] tw-justify-center tw-text-[1rem] [&:disabled]:tw-opacity-[0.6] [&:disabled]:tw-cursor-not-allowed [&:disabled]:tw-pointer-events-none nav-btn next-btn"}
                onClick={handleNext}
                disabled={!(selectedAnswers[question.id] || []).length || isSavingAnswer}
              >
                {isSavingAnswer ? (
                  <>
                    <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-1" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    Next
                    <Icon icon="mdi:chevron-right" />
                  </>
                )}
              </button>
            ) : (
              <button
                className={"tw-text-[white] tw-bg-[#28a745] [&:hover]:tw-bg-[#1976d2] [&:not(:disabled):hover]:[transform:translateY(-2px)] max-[768px]:tw-w-full max-[768px]:tw-justify-center tw-inline-flex tw-items-center tw-gap-[5px] tw-p-[8px_20px] tw-border-0 tw-rounded-[4px] tw-cursor-pointer [transition:all_0.3s_ease] tw-font-medium tw-min-w-[100px] tw-justify-center tw-text-[1rem] [&:disabled]:tw-opacity-[0.6] [&:disabled]:tw-cursor-not-allowed [&:disabled]:tw-pointer-events-none nav-btn submit-btn"}
                onClick={handleSubmit}
                disabled={!(selectedAnswers[question.id] || []).length}
              >
                Submit
                <Icon icon="mdi:check" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssignmentQuiz;
